<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="folder-upload-container">
    <div class="folder-upload-buttonbar">
      <PrimeButton
        class="select-folder-btn"
        :label="$t('selectFolder')"
        @click="triggerFileInput"
      />
    </div>
    <div
      class="folder-upload-dropzone"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop($event)"
      :class="{ 'is-dragging': isDraggingOver }"
    >
      <p>{{ $t("dropFolder") }}</p>
    </div>

    <input
      type="file"
      webkitdirectory
      @change="handleFolderSelect($event)"
      ref="fileInput"
      class="file-input-hidden"
    />
  </div>
</template>

<script>
import { mapActions } from "pinia";
import Button from "primevue/button";
import { useUploadStore } from "@/stores/upload.js";
import { useNavigationStore } from "@/stores/navigation.js";

export default {
  name: "FolderUpload",
  components: {
    PrimeButton: Button,
  },
  data() {
    return {
      isDraggingOver: false,
    };
  },
  emits: ["close"],
  methods: {
    ...mapActions(useUploadStore, ["startUpload"]),
    closePopup() {
      this.$emit("close", false);
    },
    handleFolderSelect(event) {
      const files = Array.from(event.target.files);
      const fileEntries = files.map((file) => ({
        file: file,
        relativePath: file.webkitRelativePath,
      }));
      this.processFiles(fileEntries);
    },
    processFiles(fileEntries) {
      const rootNodeId = useNavigationStore().currentNode.id;
      this.startUpload(fileEntries, rootNodeId);
      this.closePopup();
    },
    async handleDrop(event) {
      this.isDraggingOver = false;
      const items = event.dataTransfer.items;

      const fileEntries = [];
      const promises = [];

      const traverseFileTree = async (entry) => {
        if (entry.isFile) {
          return new Promise((resolve) =>
            entry.file((file) => {
              const relativePath = entry.fullPath.startsWith("/")
                ? entry.fullPath.substring(1)
                : entry.fullPath;
              fileEntries.push({ file: file, relativePath: relativePath });
              resolve();
            }),
          );
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          return new Promise((resolve) => {
            const readEntries = async () => {
              const entries = await new Promise((r) =>
                dirReader.readEntries(r),
              );
              if (entries.length > 0) {
                for (const subEntry of entries) {
                  await traverseFileTree(subEntry);
                }
                await readEntries();
              }
              resolve();
            };
            readEntries();
          });
        }
      };

      for (const item of items) {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          promises.push(traverseFileTree(entry));
        }
      }
      await Promise.all(promises);
      if (fileEntries.length > 0) {
        this.processFiles(fileEntries);
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleDragOver() {
      this.isDraggingOver = true;
    },
    handleDragLeave() {
      this.isDraggingOver = false;
    },
  },
};
</script>
<style scoped>
.folder-upload-container {
  display: flex;
  flex-direction: column;
}

.folder-upload-buttonbar {
  display: flex;
  padding: 1rem;
  border: 1px solid var(--p-surface-border);
  border-bottom: none;
  border-radius: var(--p-border-radius) var(--p-border-radius) 0 0;
  background: var(--p-content-background);
}

.select-folder-btn {
  width: 100%;
}

.file-input-hidden {
  display: none;
}

.folder-upload-dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 6rem;
  padding: 1.5rem;
  border: 1px solid var(--p-surface-border);
  border-radius: 0 0 var(--p-border-radius) var(--p-border-radius);
  background: var(--p-content-background);
  color: var(--p-text-muted-color);
  transition:
    background-color 0.2s,
    border-color 0.2s;
}

.folder-upload-dropzone p {
  margin: 0;
  text-align: center;
}

.folder-upload-dropzone.is-dragging {
  background-color: color-mix(
    in srgb,
    var(--p-primary-color) 10%,
    var(--p-content-background)
  );
  border-color: var(--p-primary-color);
}
</style>
